/**
 *  @swagger
 *  definitions:
 *    ChampionshipAdminRequestParams:
 *      type: object
 *      properties:
 *        ChampionshipId:
 *          description: ID of the associated Championship
 *          type: string
 *        Name:
 *          description: Name of the ChampionshipAdminRequest
 *          type: string
 *        Email:
 *          description: Email of the ChampionshipAdminRequest
 *          type: string
 *        Status:
 *          description: Status of the ChampionshipAdminRequest
 *          $ref: '#/components/enums/ChampionshipAdminRequestStatus'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipAdminRequestCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipAdminRequestParams'
 *        - type: object
 *          required:
 *            - ChampionshipId
 *            - Email
 *            - Status
 *
 *  @swagger
 *  definitions:
 *    ChampionshipAdminRequestQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the ChampionshipAdminRequest
 *              type: string
 *        - $ref: '#/definitions/ChampionshipAdminRequestParams'
 *
 *  @swagger
 *  definitions:
 *    ChampionshipAdminRequestUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipAdminRequestQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    ChampionshipAdminRequest:
 *      allOf:
 *        - $ref: '#/definitions/ChampionshipAdminRequestCreateParams'
 *        - $ref: '#/definitions/ChampionshipAdminRequestUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      ChampionshipAdminRequest:
 *        allOf:
 *          - $ref: '#/definitions/ChampionshipAdminRequest'
 *          - type: object
 *            properties:
 *              Championship:
 *                description: Championship of the ChampionshipAdminRequest
 *                $ref: '#/components/schemas/Championship'
 *              Admin:
 *                description: Admin of the ChampionshipAdminRequest
 *                $ref: '#/components/schemas/Admin'
 */

// Enums
import {
  ChampionshipAdminRequestStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  ChampionshipId: string;
};

export type $Params = {
  Email: string;
  Name: null | string;
  Status: ChampionshipAdminRequestStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admin?: $Model<'Admin'>;
  Championship?: $Model<'Championship'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(ChampionshipAdminRequestStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'ChampionshipAdminRequest'> => {
  const ChampionshipAdminRequest = stack.define(
    'ChampionshipAdminRequest',
    fields(Sequelize),
    {
      tableName: 'ChampionshipAdminRequests',
    },
  );

  ChampionshipAdminRequest.associate = (models: $ModelClasses) => {
    models.ChampionshipAdminRequest.belongsTo(
      models.Championship,
      {
        as: 'Championship',
        foreignKey: 'ChampionshipId',
      },
    );
    models.ChampionshipAdminRequest.belongsTo(
      models.Admin,
      {
        foreignKey: 'Email',
        targetKey: 'Email',
      },
    );
  };

  ChampionshipAdminRequest.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return ChampionshipAdminRequest;
});
